import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import {
    Badge,
    ChevronRightIcon,
    InfoSignIcon,
    Pane,
    Paragraph,
    SideSheet,
    toaster,
} from "evergreen-ui";
import { ShopifyProductVariant } from "../../../../common/models";
import { CostMetadataWithRelations } from "./CostMetadataTab";
import { CostMetadataTableRow } from "../../../../common/tables/cost-metadata/CostMetadataTableRow";
import { VariantTableRow } from "../../../../common/tables/product-listing/variants/VariantTableRow";
import { QueryInput } from "../../../../common/query-filters";
import { queryVariantsWithCostMetadata, VariantWithCostMetadataCount } from "../../datastore/ShopifyProductDataStore";
import {
    queryCostMetadataByVariantId,
    queryCostMetadataByShopifyProductId,
    useUpdateCostMetadataMutation,
} from "../../datastore/CostMetadataDataStore";
import { AbstractTableViewProvider } from "../../common/tables/view/AbstractTableViewProvider";
import { TableView } from "../../common/tables/view/TableView";
import { DefaultVariantTableConfig } from "./variants/tables/config/DefaultVariantTableConfig";
import { DefaultVariantCostMetadataSideSheetConfig } from "./variants/tables/config/DefaultVariantCostMetadataSideSheetConfig";
import { VariantTableCellFactory } from "./variants/factory/VariantTableCellFactory";
import { CostMetadataAssignmentTableCellFactory } from "./variants/factory/CostMetadataAssignmentTableCellFactory";
import { EmptyState } from "../../common/EmptyState";
import { RampartAuthorized } from "../../rampart/RampartAuthorized";
import { sortRows } from "../../common/tables/etc/TableUtils";

export const ProductVariantsTab: FC<{
    productListingId: string;
    shopifyProductId?: string;
}> = ({ productListingId, shopifyProductId }) => {
    if (!shopifyProductId) {
        return (
            <Pane padding={24}>
                <EmptyState
                    title="No Shopify product linked"
                    subtitle="This product listing is not linked to a Shopify product"
                >
                    <InfoSignIcon size={60} />
                </EmptyState>
            </Pane>
        );
    }

    return (
        <RampartAuthorized requiredGroups={["juniper.self-serve.admin"]}>
            <ProductVariantsContent
                productListingId={productListingId}
                shopifyProductId={shopifyProductId}
            />
        </RampartAuthorized>
    );
};

const ProductVariantsContent: FC<{
    productListingId: string;
    shopifyProductId: string;
}> = ({ productListingId, shopifyProductId }) => {
    const [selectedVariant, setSelectedVariant] = useState<VariantWithCostMetadataCount | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);

    const variantTableCellFactory = useMemo(() => {
        return new VariantTableCellFactory({
            onCostMetadataClick: (rowData) => setSelectedVariant(rowData),
        });
    }, []);

    const queryTableData = useCallback(async (input: QueryInput<VariantTableRow>) => {
        const variants = await queryVariantsWithCostMetadata(productListingId);
        return sortRows(variants, input.sortColumn, input.sortOrder);
    }, [productListingId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const onUpdateRow = useCallback((
        variants: VariantWithCostMetadataCount[],
    ): VariantWithCostMetadataCount[] => {
        return variants;
    }, []);

    const emptyViewProps = useMemo(() => ({
        primaryText: "No variants found",
        secondaryText: "This product has no Shopify variants",
    }), []);

    const errorViewProps = useMemo(() => ({
        primaryText: "Failed to load variants",
        secondaryText: "Refresh the page to try again",
    }), []);

    return (
        <>
            <AbstractTableViewProvider
                key={refreshKey}
                queryTableData={queryTableData}
                onUpdateRow={onUpdateRow}
                updateRowLoading={false}
                updateRowCalled={false}
                updateRowError={false}
                tableCellFactory={variantTableCellFactory}
                savedTableConfigProps={{
                    tableName: "productVariants",
                    identifiers: ["v3"],
                    defaultConfigValue: DefaultVariantTableConfig,
                }}
            >
                <TableView
                    emptyViewProps={emptyViewProps}
                    errorViewProps={errorViewProps}
                />
            </AbstractTableViewProvider>
            <VariantCostMetadataSideSheet
                variant={selectedVariant}
                shopifyProductId={shopifyProductId}
                onDismiss={() => setSelectedVariant(null)}
                onDataChanged={() => {
                    setSelectedVariant(null);
                    setRefreshKey(k => k + 1);
                }}
            />
        </>
    );
};

// ---------------------------------------------------------------------------
// Side Sheet: CostMetadata with checkbox assignment for variant
// ---------------------------------------------------------------------------

type CostMetadataWithAssignment = CostMetadataWithRelations & {
    _assigned: boolean;
    _lastVariant: boolean;
};

interface VariantCostMetadataSideSheetProps {
    variant: VariantWithCostMetadataCount | null;
    shopifyProductId: string;
    onDismiss: () => void;
    onDataChanged: () => void;
}

const VariantCostMetadataSideSheet: FC<VariantCostMetadataSideSheetProps> = ({
    variant,
    shopifyProductId,
    onDismiss,
    onDataChanged,
}) => {
    const [dirty, setDirty] = useState(false);

    const [
        updateCostMetadata,
        { error: assignmentMutationError },
    ] = useUpdateCostMetadataMutation();

    useEffect(() => {
        if (assignmentMutationError) {
            toaster.danger("Failed to update cost metadata assignment. Please try again.");
        }
    }, [assignmentMutationError]);

    const costMetadataTableCellFactory = useMemo(() => {
        return new CostMetadataAssignmentTableCellFactory();
    }, []);

    const queryTableData = useCallback(async (_input: QueryInput<CostMetadataTableRow>): Promise<CostMetadataWithAssignment[]> => {
        if (!variant) return [];
        const [variantCm, allCm] = await Promise.all([
            queryCostMetadataByVariantId(String(variant.id)),
            queryCostMetadataByShopifyProductId({
                shopifyProductId,
                options: { take: 200 },
            }),
        ]);
        const assignedIds = new Set(variantCm.map(cm => cm.id));
        return allCm.map(cm => {
            const data = cm as CostMetadataWithRelations;
            const variantCount = (data.shopifyProductVariant ?? []).length;
            return {
                ...data,
                _assigned: assignedIds.has(cm.id),
                _lastVariant: variantCount <= 1,
                hasAllVariantsCovered: assignedIds.has(cm.id),
            };
        });
    }, [variant, shopifyProductId]);

    const onUpdateRow = useCallback((
        cmList: CostMetadataWithAssignment[],
        id: string | number,
        columnName: keyof CostMetadataTableRow,
        value: string | number | boolean | null,
    ): CostMetadataWithAssignment[] => {
        if (columnName !== "hasAllVariantsCovered" || !variant) {
            return cmList;
        }

        const isAssigning = Boolean(value);
        const cm = cmList.find(c => c.id === id);
        if (!cm) return cmList;

        const existingVariants: Pick<ShopifyProductVariant, 'id' | 'sku' | 'title'>[] = cm.shopifyProductVariant ?? [];
        const existingVariantIds = existingVariants.map(v => String(v.id));

        // Prevent unassigning if this is the last variant on the CostMetadata
        if (!isAssigning && existingVariantIds.length <= 1) {
            return cmList;
        }

        let newVariants: Pick<ShopifyProductVariant, 'id' | 'sku' | 'title'>[];
        if (isAssigning) {
            const newVariantIds = [...existingVariantIds, String(variant.id)];
            // TODO: Add optimistic rollback on mutation failure (see #1297)
            updateCostMetadata({ id: cm.id, variantIds: newVariantIds });
            newVariants = [...existingVariants, { id: String(variant.id), sku: variant.sku, title: variant.title } as ShopifyProductVariant];
        } else {
            const newVariantIds = existingVariantIds.filter(vid => vid !== String(variant.id));
            // TODO: Add optimistic rollback on mutation failure (see #1297)
            updateCostMetadata({ id: cm.id, variantIds: newVariantIds });
            newVariants = existingVariants.filter(v => String(v.id) !== String(variant.id));
        }

        setDirty(true);
        return cmList.map(c => {
            if (c.id !== id) return c;
            return {
                ...c,
                _assigned: isAssigning,
                _lastVariant: newVariants.length <= 1,
                hasAllVariantsCovered: isAssigning,
                shopifyProductVariant: newVariants as ShopifyProductVariant[],
            };
        });
    }, [variant, updateCostMetadata]);

    const handleClose = useCallback(() => {
        if (dirty) {
            onDataChanged();
        }
        onDismiss();
    }, [dirty, onDataChanged, onDismiss]);

    const emptyViewProps = useMemo(() => ({
        primaryText: "No cost metadata found",
        secondaryText: "This product has no cost metadata records",
    }), []);

    const errorViewProps = useMemo(() => ({
        primaryText: "Failed to load cost metadata",
        secondaryText: "Refresh the page to try again",
    }), []);

    if (!variant) return null;

    return (
        <SideSheet
            width="65vw"
            isShown={!!variant}
            onCloseComplete={handleClose}
        >
            <Pane paddingBottom={8}>
                <h3 className="multi-tab-heading" style={{ marginBottom: 0 }}>
                    <span className="multi-tab-breadcrumb-parent">Product Variants</span>
                    <ChevronRightIcon color="muted" size={16} marginX={4} style={{ verticalAlign: 'middle' }} />
                    Cost Metadata
                </h3>
                <Pane display="flex" flexWrap="wrap" alignItems="center" gap={8} paddingLeft={36} marginTop={16} marginBottom={24}>
                    <Badge color="neutral" textTransform="none" fontWeight="normal" paddingX={10} borderRadius={10}>
                        <strong>Variant ID</strong>&ensp;{variant.id}
                    </Badge>
                    {variant.sku && (
                        <Badge color="neutral" textTransform="none" fontWeight="normal" paddingX={10} borderRadius={10}>
                            <strong>Variant SKU</strong>&ensp;{variant.sku}
                        </Badge>
                    )}
                    {variant.title && variant.title !== variant.sku && (
                        <Badge color="neutral" textTransform="none" fontWeight="normal" paddingX={10} borderRadius={10}>
                            <strong>Variant Title</strong>&ensp;{variant.title}
                        </Badge>
                    )}
                </Pane>
                <Paragraph size={300} color="muted" paddingLeft={36} marginTop={12} marginBottom={0}>
                    Toggle the checkbox in any one of the <strong><code>CostMetadata</code></strong> rows below to assign it to this selected <strong><code>ProductVariant</code></strong>.
                </Paragraph>
            </Pane>
            <AbstractTableViewProvider
                queryTableData={queryTableData}
                onUpdateRow={onUpdateRow}
                updateRowLoading={false}
                updateRowCalled={false}
                updateRowError={false}
                tableCellFactory={costMetadataTableCellFactory}
                savedTableConfigProps={{
                    tableName: "variantCostMetadataSideSheet",
                    identifiers: ["v3"],
                    defaultConfigValue: DefaultVariantCostMetadataSideSheetConfig,
                }}
            >
                <TableView
                    emptyViewProps={emptyViewProps}
                    errorViewProps={errorViewProps}
                />
            </AbstractTableViewProvider>
        </SideSheet>
    );
};
